import { useState } from "react";
import { Star, Loader2, CheckCircle2, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const CATEGORIES = [
  "Subtitle quality",
  "Translation quality",
  "Editor experience",
  "Speed",
  "Pricing",
  "Something else",
];

export const FeedbackForm = () => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("none");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Pick a rating first");
    if (message.trim().length < 1) return toast.error("Tell us a little more");
    setBusy(true);
    try {
      await api.post("/support/feedback", {
        rating,
        message: message.trim(),
        category: category === "none" ? null : category,
      });
      setDone(true);
      toast.success("Thanks — feedback sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send feedback");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="border border-emerald-500/30 bg-emerald-500/5 p-8 text-center"
        data-testid="feedback-success"
      >
        <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-4" />
        <h2 className="font-heading text-2xl font-bold tracking-tight text-white">
          Feedback received
        </h2>
        <p className="text-sm text-zinc-400 mt-2">
          It goes straight to the people building this. No ticket was created.
        </p>
        <button
          type="button"
          onClick={() => { setDone(false); setRating(0); setMessage(""); setCategory("none"); }}
          data-testid="feedback-again-btn"
          className="mt-6 text-[10px] font-mono uppercase tracking-widest border border-zinc-800 hover:bg-zinc-900 text-zinc-300 px-4 py-2 transition-colors"
        >
          Send more feedback
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-zinc-800 bg-zinc-900/40 p-6 md:p-8 space-y-7" data-testid="feedback-form">
      <div>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">
          <MessageSquare className="w-3 h-3" /> Send feedback
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white">
          How is LinguaSub treating you?
        </h2>
        <p className="text-sm text-zinc-400 mt-2">
          Quick and lightweight — this does not open a support ticket.
        </p>
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
          Rating
        </label>
        <div className="flex items-center gap-2" data-testid="feedback-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setRating(n)}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              data-testid={`feedback-star-${n}`}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
              className="p-1 transition-transform duration-150 hover:scale-110"
            >
              <Star
                className={`w-7 h-7 transition-colors duration-150 ${
                  n <= (hover || rating)
                    ? "text-amber-400 fill-amber-400"
                    : "text-zinc-700"
                }`}
              />
            </button>
          ))}
          {rating > 0 && (
            <span className="ml-2 text-xs font-mono text-zinc-500" data-testid="feedback-rating-value">
              {rating}/5
            </span>
          )}
        </div>
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Category <span className="text-zinc-600">(optional)</span>
        </label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger
            data-testid="feedback-category-select"
            className="bg-zinc-950 border-zinc-800 rounded-none h-11 text-white"
          >
            <SelectValue placeholder="Pick one" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-white rounded-none">
            <SelectItem value="none">No category</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
          Your feedback
        </label>
        <Textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          maxLength={2000}
          data-testid="feedback-message-input"
          placeholder="What worked, what didn't, what you wish it did…"
          className="bg-zinc-950 border-zinc-800 rounded-none text-white placeholder:text-zinc-600 resize-none"
        />
        <div className="text-[10px] font-mono text-zinc-600 mt-1 text-right">
          {message.length}/2000
        </div>
      </div>

      <button
        type="submit"
        disabled={busy}
        data-testid="feedback-submit-btn"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 disabled:opacity-50 px-6 py-3 transition-colors"
      >
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
        Send feedback
      </button>
    </form>
  );
};
